import { Link } from "react-router-dom";
import { Ticket, Gift, UserPlus, Zap } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { StatusBadge, GradeBadge } from "@/components/common";

export default function DropCard({ drop, index = 0 }) {
  const isRef = drop.is_referral;
  const filled = isRef ? (drop.referral_entries || 0) : drop.claimed_seats;
  const pct = drop.total_seats ? Math.round((filled / drop.total_seats) * 100) : 0;
  const free = drop.is_giveaway;
  const giveaway = free || isRef;
  const remaining = Math.max(0, drop.total_seats - filled);
  const hot = drop.status === "active" && pct >= 50 && remaining > 0;
  const cta = drop.status === "drawn" ? "View Results" : drop.status === "drawing" || drop.status === "full" ? "View Drop" : giveaway ? "Enter Giveaway" : "Enter Drop";

  return (
    <Link to={`/drops/${drop.id}`} data-testid={`drop-card-${drop.id}`}
      className="group card-surface rounded-3xl p-2.5 sm:p-3 flex flex-col hover:border-gold/40 transition-colors animate-rise"
      style={{ animationDelay: `${index * 60}ms` }}>
      {/* Image */}
      <div className="relative aspect-square rounded-2xl bg-vault overflow-hidden">
        {drop.prize_photo ? (
          <img src={fileUrl(drop.prize_photo)} alt={drop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-600"><Ticket className="w-12 h-12" /></div>
        )}
        {/* top scrim for badge legibility */}
        <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
        <div className="absolute top-2.5 left-2.5"><StatusBadge status={drop.status} /></div>
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
          {hot && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full gold-gradient text-obsidian text-[10px] font-black uppercase tracking-wide gold-glow" data-testid={`drop-hot-${drop.id}`}>
              <Zap className="w-3 h-3" fill="currentColor" /> Hot
            </span>
          )}
          <GradeBadge grade={[drop.brand, drop.grade].filter(Boolean).join(" ")} />
        </div>
        {giveaway && (
          <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-obsidian/85 backdrop-blur-sm border border-gold/40 text-gold text-[9px] font-bold uppercase tracking-wide whitespace-nowrap shadow-lg">
            {drop.is_referral ? <><UserPlus className="w-2.5 h-2.5" /> Referral</> : <><Gift className="w-2.5 h-2.5" /> Free Giveaway</>}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 pt-3 px-1">
        <h3 className="font-display font-black text-white text-base sm:text-lg leading-tight line-clamp-2 min-h-[2.5rem]">{drop.title}</h3>
        {drop.description && <p className="mt-1 text-xs sm:text-sm text-slate-400 leading-snug line-clamp-2 min-h-[2.1rem]">{drop.description}</p>}

        <div className="mt-2.5 flex items-baseline gap-1">
          {isRef ? (
            <span className="font-mono text-base sm:text-lg font-black text-gold leading-none">Invite to enter</span>
          ) : (
            <>
              <span className="font-mono text-xl sm:text-2xl font-black text-gold leading-none">{free ? "FREE" : `$${drop.ticket_price?.toFixed(2)}`}</span>
              {!free && <span className="text-slate-500 text-xs sm:text-sm font-sans">/ ticket</span>}
            </>
          )}
        </div>

        <div className="mt-2.5 h-1.5 rounded-full bg-vault overflow-hidden">
          <div className="h-full gold-gradient rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] sm:text-xs font-mono whitespace-nowrap">
          <span className="text-slate-400"><span className="text-white font-bold">{filled}</span> {isRef ? "entries" : "sold"}</span>
          <span className="text-slate-400"><span className="text-white font-bold">{remaining}</span> left</span>
        </div>

        <div className="mt-3 mt-auto pt-3">
          <span className="block w-full text-center gold-gradient text-obsidian font-display font-black tracking-wide text-sm sm:text-base py-3 rounded-xl group-hover:brightness-110 transition-all" data-testid={`drop-cta-${drop.id}`}>
            {cta}
          </span>
        </div>
      </div>
    </Link>
  );
}
